/**
 * SplinkQrCard — reusable "short URL + QR" card.
 *
 * Drop into any public detail page (business, product, deal, article…).
 * Auto-calls POST /api/splink/for-page which lazily creates + caches a
 * short link on the backend, so repeat visits are free.
 *
 * Props
 *  - `longUrl`   full absolute URL to shorten (required)
 *  - `label`     optional human label sent to SpLink for the dashboard
 *  - `site`      SpLink tenant slug — defaults to "foodpulse"
 *  - `title`     small header above the card (default "Share this page")
 *  - `subtitle`  helper copy under the header (default: scan-to-open hint)
 */
import { useEffect, useState } from "react";
import { Copy, Check, Loader2, Link as LinkIcon, QrCode } from "lucide-react";
import { http } from "@/lib/api";
import { normalizeShortUrl, normalizeAssetUrl, synthesizeQrFromShortUrl, displayShortUrl } from "@/lib/splinkUrl";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";

export default function SplinkQrCard({
  longUrl,
  label,
  site = "foodpulse",
  title = "Share this page",
  subtitle = "Scan the QR to open on any phone — or copy the short link.",
}) {
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(true);
  const [err, setErr]       = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!longUrl) { setBusy(false); return; }
    setBusy(true); setErr(null);
    http.post("/splink/for-page", { long_url: longUrl, label, site })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Could not build short link"); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [longUrl, label, site]);

  // Defensive rewrite: any legacy `fp.to` value from a cached backend
  // response gets rewritten to `splink.pro`, and relative QR paths get
  // apiBase-prefixed. Absolute SpLink QR URLs pass through untouched.
  const shortUrl = normalizeShortUrl(data?.short_url);
  const qrUrl    = normalizeAssetUrl(
    data?.qr_code_url || synthesizeQrFromShortUrl(shortUrl),
    API_BASE,
  );

  const copy = async () => {
    if (!shortUrl) return;
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* older browsers — silently drop */ }
  };

  return (
    <aside
      className="rounded-2xl border p-4 flex items-center gap-4"
      style={{ borderColor: "#E9D8B4", background: CREAM }}
      data-testid="splink-qr-card"
    >
      <div className="flex-shrink-0 rounded-xl bg-white p-2 border" style={{ borderColor: "#E9D8B4" }}>
        {busy ? (
          <div className="w-[96px] h-[96px] flex items-center justify-center"><Loader2 className="animate-spin" size={24} /></div>
        ) : qrUrl ? (
          <img src={qrUrl} alt="QR code" width={96} height={96} data-testid="splink-qr-img" />
        ) : (
          <div className="w-[96px] h-[96px] flex items-center justify-center text-gray-400"><QrCode size={28} /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: PURPLE }}>{title}</div>
        <div className="text-xs text-gray-700 mb-2">{subtitle}</div>
        {err ? (
          <div className="text-xs text-red-600" data-testid="splink-error">{err}</div>
        ) : shortUrl ? (
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={shortUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-sm truncate hover:underline"
              style={{ color: GREEN }}
              data-testid="splink-short-url"
            >
              {displayShortUrl(shortUrl)}
            </a>
            <button
              onClick={copy}
              className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-2.5 py-1 border"
              style={{ borderColor: GREEN, color: copied ? GREEN : GREEN, background: copied ? "#DCFCE7" : "white" }}
              data-testid="splink-copy-btn"
            >
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
            <a
              href={qrUrl}
              download="qr.png"
              className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-2.5 py-1"
              style={{ background: AMBER, color: "white" }}
              data-testid="splink-download-qr"
            >
              <LinkIcon size={12} /> Download QR
            </a>
          </div>
        ) : (
          <div className="text-xs text-gray-500">No short link yet.</div>
        )}
      </div>
    </aside>
  );
}
